import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bell, Lock, Mail, Plus, RotateCcw, Save, Send, X } from 'lucide-react';
import toast from 'react-hot-toast';
import {
  notificacoesConfigService,
  PerfilChave,
  PreferenciasData,
  TipoPreferencia,
  TipoPreferenciaEdicao,
} from '../../services/notificacoesConfig';
import { mensagemDoErro } from '../../utils/erros';
import { ROTULO_MODULO } from '../../utils/catalogoDePermissoes';
import { useConfirm } from '../../components/ConfirmDialog';

/**
 * Gestão de notificações por empresa.
 *
 * Um cartão por tipo de aviso (ex.: "Motorista pendente de aprovação"). Cada
 * cartão tem duas configurações independentes:
 *  - quais perfis veem o aviso dentro do sistema (sininho);
 *  - se o aviso também sai por e-mail, e para quais endereços.
 *
 * Segue o mesmo padrão de PermissoesList.tsx: um rascunho local comparado ao
 * que veio do servidor para habilitar "Salvar alterações" só quando há
 * mudança de fato, evitando salvar um "não-alterado" que marcar/desmarcar a
 * mesma caixa produziria.
 *
 * CONTRATO DE UI (estável, os testes dependem destes nomes):
 *  - cartão do tipo:        data-testid="cartao-tipo-{tipo}"
 *  - caixa de perfil:       data-testid="perfil-{tipo}-{perfil}", aria-label="{rotuloPerfil} recebe {rotuloTipo}"
 *  - interruptor de e-mail: role="switch" aria-checked data-testid="email-ativo-{tipo}"
 *  - chip de e-mail:        data-testid="chip-email-{tipo}-{email}"
 *  - remover chip:          aria-label="Remover {email}"
 *  - salvar (topo):         data-testid="salvar-preferencias"
 *  - enviar teste (cartão): data-testid="enviar-teste-{tipo}"
 */

const paraEdicao = (t: TipoPreferencia): TipoPreferenciaEdicao => ({
  tipo: t.tipo,
  perfis: { ...t.perfis },
  email_ativo: t.email_ativo,
  emails: t.emails.map((e) => ({ ...e })),
});

/**
 * Rótulo do módulo de origem do aviso (usado no texto do cadeado quando um
 * perfil está bloqueado). A API já manda `modulo_rotulo` pronto; o mapa local
 * é só uma rede de segurança para não desenhar a chave crua ("aprovacoes")
 * caso esse campo venha vazio em algum ambiente mais antigo do backend.
 */
const rotuloDoModulo = (t: TipoPreferencia): string =>
  t.modulo_rotulo || ROTULO_MODULO[t.modulo] || t.modulo;

interface CartaoTipoPreferencaProps {
  tipoServidor: TipoPreferencia;
  perfis: Record<PerfilChave, string>;
  rascunho: TipoPreferenciaEdicao;
  alterado: boolean;
  limiteEmails: number;
  enviandoTeste: boolean;
  restaurando: boolean;
  onAlternarPerfil: (perfil: PerfilChave) => void;
  onAlternarEmailAtivo: () => void;
  onAdicionarEmail: (email: string) => void;
  onRemoverEmail: (email: string) => void;
  onEnviarTeste: () => void;
  onRestaurar: () => void;
}

const CartaoTipoPreferencia: React.FC<CartaoTipoPreferencaProps> = ({
  tipoServidor, perfis, rascunho, alterado, limiteEmails, enviandoTeste, restaurando,
  onAlternarPerfil, onAlternarEmailAtivo, onAdicionarEmail, onRemoverEmail,
  onEnviarTeste, onRestaurar,
}) => {
  const [novoEmail, setNovoEmail] = useState('');
  const limiteAtingido = rascunho.emails.length >= limiteEmails;
  const perfisChaves = Object.keys(perfis) as PerfilChave[];

  const adicionar = () => {
    const valor = novoEmail.trim();
    if (!valor || limiteAtingido) return;
    onAdicionarEmail(valor);
    setNovoEmail('');
  };

  return (
    <div
      data-testid={`cartao-tipo-${tipoServidor.tipo}`}
      className="bg-white rounded-lg border p-4 space-y-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold text-gray-900">{tipoServidor.rotulo}</h3>
          <p className="text-xs text-gray-500 mt-0.5">{tipoServidor.descricao}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {tipoServidor.personalizado && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              Personalizado
            </span>
          )}
          <button
            type="button"
            onClick={onRestaurar}
            disabled={!tipoServidor.personalizado || restaurando}
            title={tipoServidor.personalizado ? 'Voltar ao padrão do sistema' : 'Este aviso já está no padrão'}
            data-testid={`restaurar-tipo-${tipoServidor.tipo}`}
            className="inline-flex items-center gap-1 px-2 py-1 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Restaurar padrão
          </button>
        </div>
      </div>

      <fieldset>
        <legend className="text-xs font-semibold text-gray-600 mb-2">Quem recebe este aviso no sistema</legend>
        <div className="flex flex-wrap gap-x-5 gap-y-2">
          {perfisChaves.map((perfil) => {
            const bloqueado = tipoServidor.perfis_bloqueados.includes(perfil);
            return (
              <div key={perfil} className="flex flex-col">
                <label className="inline-flex items-center gap-1.5 cursor-pointer text-sm text-gray-700">
                  <input
                    type="checkbox"
                    checked={Boolean(rascunho.perfis[perfil])}
                    onChange={() => onAlternarPerfil(perfil)}
                    disabled={bloqueado}
                    aria-label={`${perfis[perfil]} recebe ${tipoServidor.rotulo}`}
                    data-testid={`perfil-${tipoServidor.tipo}-${perfil}`}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-60"
                  />
                  {perfis[perfil]}
                  {bloqueado && <Lock className="h-3 w-3 text-gray-400" aria-hidden />}
                </label>
                {bloqueado && (
                  <span className="text-xs text-gray-400 ml-6">
                    {perfis[perfil]} não pode consultar {rotuloDoModulo(tipoServidor)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </fieldset>

      <div className="border-t pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600">
            <Mail className="h-3.5 w-3.5" /> Enviar também por e-mail
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={rascunho.email_ativo}
            aria-label={`Envio por e-mail de ${tipoServidor.rotulo}`}
            data-testid={`email-ativo-${tipoServidor.tipo}`}
            onClick={onAlternarEmailAtivo}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              rascunho.email_ativo ? 'bg-blue-600' : 'bg-gray-300'}`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                rascunho.email_ativo ? 'translate-x-[18px]' : 'translate-x-1'}`}
            />
          </button>
        </div>

        {rascunho.email_ativo && (
          <>
            {tipoServidor.usando_padrao_de_emails && (
              <p className="text-xs text-gray-400">Usando a lista padrão de e-mails do sistema.</p>
            )}
            <div className="flex flex-wrap gap-1.5">
              {rascunho.emails.map((destinatario) => (
                <span
                  key={destinatario.email}
                  data-testid={`chip-email-${tipoServidor.tipo}-${destinatario.email}`}
                  className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-gray-100 text-xs text-gray-700"
                >
                  {destinatario.nome ? `${destinatario.nome} <${destinatario.email}>` : destinatario.email}
                  <button
                    type="button"
                    aria-label={`Remover ${destinatario.email}`}
                    onClick={() => onRemoverEmail(destinatario.email)}
                    className="p-0.5 rounded-full hover:bg-gray-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {rascunho.emails.length === 0 && (
                <span className="text-xs text-amber-700">
                  Nenhum e-mail informado — o envio ficará desligado ao salvar.
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={novoEmail}
                onChange={(e) => setNovoEmail(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); adicionar(); } }}
                disabled={limiteAtingido}
                placeholder={limiteAtingido ? `Limite de ${limiteEmails} e-mails atingido` : 'novo@email.com'}
                aria-label={`Adicionar e-mail para ${tipoServidor.rotulo}`}
                data-testid={`input-email-${tipoServidor.tipo}`}
                className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md disabled:bg-gray-50"
              />
              <button
                type="button"
                onClick={adicionar}
                disabled={!novoEmail.trim() || limiteAtingido}
                data-testid={`adicionar-email-${tipoServidor.tipo}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 hover:bg-blue-50 rounded-md disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Plus className="h-3.5 w-3.5" /> Adicionar
              </button>
            </div>
          </>
        )}
      </div>

      <div className="border-t pt-3 flex items-center justify-between">
        {alterado && <span className="text-xs text-amber-700">Alterações não salvas.</span>}
        <button
          type="button"
          onClick={onEnviarTeste}
          disabled={alterado || enviandoTeste}
          title={alterado ? 'Salve as alterações antes de enviar um teste.' : undefined}
          data-testid={`enviar-teste-${tipoServidor.tipo}`}
          className="ml-auto inline-flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-blue-600"
        >
          <Send className="h-3.5 w-3.5" /> {enviandoTeste ? 'Enviando...' : 'Enviar teste'}
        </button>
      </div>
    </div>
  );
};

const NotificacoesConfig: React.FC = () => {
  const confirm = useConfirm();
  const [preferencias, setPreferencias] = useState<PreferenciasData | null>(null);
  const [rascunho, setRascunho] = useState<Record<string, TipoPreferenciaEdicao>>({});
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [enviandoTeste, setEnviandoTeste] = useState<Record<string, boolean>>({});
  const [restaurando, setRestaurando] = useState<Record<string, boolean>>({});

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const res = await notificacoesConfigService.buscar();
      setPreferencias(res.data);
      setRascunho(Object.fromEntries(res.data.tipos.map((t) => [t.tipo, paraEdicao(t)])));
    } catch (e: any) {
      setErro(mensagemDoErro(e, 'Não foi possível carregar as preferências de notificação.'));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const servidorPorTipo = useMemo(() => {
    const mapa: Record<string, TipoPreferencia> = {};
    (preferencias?.tipos || []).forEach((t) => { mapa[t.tipo] = t; });
    return mapa;
  }, [preferencias]);

  // Mesma técnica de PermissoesList: comparar o rascunho ao que veio do
  // servidor evita habilitar "Salvar" quando a pessoa desfaz a própria mudança.
  const alteradoDoTipo = useCallback((tipo: string): boolean => {
    const servidor = servidorPorTipo[tipo];
    const atual = rascunho[tipo];
    if (!servidor || !atual) return false;
    return JSON.stringify(atual) !== JSON.stringify(paraEdicao(servidor));
  }, [rascunho, servidorPorTipo]);

  const alteradoGeral = useMemo(
    () => Object.keys(rascunho).some(alteradoDoTipo),
    [rascunho, alteradoDoTipo],
  );

  const atualizarRascunho = (tipo: string, mudar: (atual: TipoPreferenciaEdicao) => TipoPreferenciaEdicao) => {
    setRascunho((estado) => ({ ...estado, [tipo]: mudar(estado[tipo]) }));
  };

  const alternarPerfil = (tipo: string, perfil: PerfilChave) => {
    atualizarRascunho(tipo, (atual) => ({
      ...atual,
      perfis: { ...atual.perfis, [perfil]: !atual.perfis[perfil] },
    }));
  };

  const alternarEmailAtivo = (tipo: string) => {
    atualizarRascunho(tipo, (atual) => ({ ...atual, email_ativo: !atual.email_ativo }));
  };

  const adicionarEmail = (tipo: string, email: string) => {
    atualizarRascunho(tipo, (atual) => (
      atual.emails.some((e) => e.email === email)
        ? atual
        : { ...atual, emails: [...atual.emails, { email, nome: '' }] }
    ));
  };

  const removerEmail = (tipo: string, email: string) => {
    atualizarRascunho(tipo, (atual) => ({
      ...atual,
      emails: atual.emails.filter((e) => e.email !== email),
    }));
  };

  const salvar = async () => {
    const alterados = Object.keys(rascunho).filter(alteradoDoTipo).map((tipo) => rascunho[tipo]);
    if (alterados.length === 0) return;
    setSalvando(true);
    try {
      const res = await notificacoesConfigService.salvar({ tipos: alterados });
      toast.success(res.message);
      setPreferencias((atual) => (atual ? { ...atual, tipos: res.data.tipos } : atual));
      setRascunho(Object.fromEntries(res.data.tipos.map((t) => [t.tipo, paraEdicao(t)])));
    } catch (e: any) {
      toast.error(mensagemDoErro(e, 'Não foi possível salvar as preferências.'));
    } finally {
      setSalvando(false);
    }
  };

  const restaurar = async (tipo: string) => {
    const rotulo = servidorPorTipo[tipo]?.rotulo || tipo;
    if (!(await confirm({
      title: 'Restaurar o padrão',
      message: `As configurações de "${rotulo}" voltam ao padrão do sistema e as alterações feitas pela empresa são descartadas.`,
      confirmLabel: 'Restaurar',
      variant: 'danger',
    }))) return;

    setRestaurando((estado) => ({ ...estado, [tipo]: true }));
    try {
      const res = await notificacoesConfigService.restaurar(tipo);
      toast.success(res.message || 'Aviso restaurado ao padrão.');
      setPreferencias((atual) => (atual
        ? { ...atual, tipos: atual.tipos.map((t) => (t.tipo === tipo ? res.data : t)) }
        : atual));
      setRascunho((atual) => ({ ...atual, [tipo]: paraEdicao(res.data) }));
    } catch (e: any) {
      toast.error(mensagemDoErro(e, 'Não foi possível restaurar este aviso.'));
    } finally {
      setRestaurando((estado) => ({ ...estado, [tipo]: false }));
    }
  };

  const enviarTeste = async (tipo: string) => {
    setEnviandoTeste((estado) => ({ ...estado, [tipo]: true }));
    try {
      const res = await notificacoesConfigService.enviarTeste(tipo);
      toast.success(res.message);
    } catch (e: any) {
      toast.error(mensagemDoErro(e, 'Não foi possível enviar o teste.'));
    } finally {
      setEnviandoTeste((estado) => ({ ...estado, [tipo]: false }));
    }
  };

  if (carregando) {
    return <div className="p-10 text-center text-gray-500">Carregando...</div>;
  }

  if (erro || !preferencias) {
    return (
      <div role="alert" className="bg-white border rounded-lg p-8 text-center">
        <p className="text-red-600 mb-3">{erro || 'Preferências de notificação indisponíveis.'}</p>
        <button onClick={carregar} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Bell className="h-6 w-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-gray-900">Notificações</h1>
          </div>
          <p className="text-gray-600 mt-1">
            Escolha quem vê cada aviso no sistema e para quais e-mails ele também é enviado.
          </p>
        </div>
        <button
          type="button"
          onClick={salvar}
          disabled={!alteradoGeral || salvando}
          data-testid="salvar-preferencias"
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Save className="h-4 w-4" /> {salvando ? 'Salvando...' : 'Salvar alterações'}
        </button>
      </div>

      {preferencias.tipos.length === 0 ? (
        <div className="bg-white border rounded-lg p-8 text-center text-gray-500">
          Nenhum tipo de aviso configurável no momento.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {preferencias.tipos.map((tipoServidor) => (
            <CartaoTipoPreferencia
              key={tipoServidor.tipo}
              tipoServidor={tipoServidor}
              perfis={preferencias.perfis}
              rascunho={rascunho[tipoServidor.tipo] || paraEdicao(tipoServidor)}
              alterado={alteradoDoTipo(tipoServidor.tipo)}
              limiteEmails={preferencias.limite_emails}
              enviandoTeste={Boolean(enviandoTeste[tipoServidor.tipo])}
              restaurando={Boolean(restaurando[tipoServidor.tipo])}
              onAlternarPerfil={(perfil) => alternarPerfil(tipoServidor.tipo, perfil)}
              onAlternarEmailAtivo={() => alternarEmailAtivo(tipoServidor.tipo)}
              onAdicionarEmail={(email) => adicionarEmail(tipoServidor.tipo, email)}
              onRemoverEmail={(email) => removerEmail(tipoServidor.tipo, email)}
              onEnviarTeste={() => enviarTeste(tipoServidor.tipo)}
              onRestaurar={() => restaurar(tipoServidor.tipo)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default NotificacoesConfig;
